import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Map, Package, Briefcase, GraduationCap, Plus, ArrowRight, X, Send, Activity, BookOpen, Brain, BadgePercent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import logoFeo from "@/assets/feo/logo-feo.png";
import { AdminGate } from "@/components/admin/AdminGate";

export const Route = createFileRoute("/admin/planos")({
  head: () => ({
    meta: [
      { title: "Admin — Projetos/Implementações" },
      { name: "description", content: "Planos e implementações da FEO, incluindo a operação BLACKFEO 2026." },
      { property: "og:title", content: "FEO · Planos e implementações" },
      { property: "og:description", content: "Controle dos projetos e ações da FEO." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/planos">
      <AdminProjetosPage />
    </AdminGate>
  ),
});

type ProjetoStatus = "concluido" | "emandamento" | "futuro";

const projetos: { title: string; desc: string; to: string; icon: typeof Map; status: ProjetoStatus }[] = [
  { title: "BLACKFEO", desc: "Ação futura para novembro inteiro, com catálogo completo e todos os produtos em venda direta com desconto.", to: "/admin/plano-black-november", icon: BadgePercent, status: "emandamento" },
  { title: "Migração RD CRM → FEO CRM", desc: "Todos os leads de todos os fluxos no FEO CRM, divididos entre as 6 vendedoras. Concluída em 04/11/2026: 3 funis, RD CRM sem novos leads.", to: "/admin/plano-migracao-feo-crm", icon: Send, status: "concluido" },
  { title: "E-FISIO — Blog", desc: "Migração do blog e-fisio.wiki.br (WordPress) para /e-fisio no Lovable dentro do domínio principal + área admin do blog.", to: "/admin/plano-e-fisio", icon: BookOpen, status: "concluido" },
  { title: "Páginas da FEO no Lovable", desc: "Plano master de todas as páginas: institucionais, catálogos, LPs de venda, funis de captura e blog — com checklist de progresso.", to: "/admin/plano-paginas", icon: Package, status: "emandamento" },
  { title: "B2B — FEO Clínicas", desc: "LP /feoclinicas com captura própria (planos Livre e Guiado) + mini-CRM kanban em /admin/feoclinicas com acesso separado.", to: "/admin/plano-b2b", icon: Briefcase, status: "emandamento" },
  { title: "Migração de domínio e infra", desc: "Virada de domínio, redirecionamentos, SEO e integrações. Concluído, mantido como histórico.", to: "/admin/plano-migracao", icon: Map, status: "concluido" },
  { title: "LEADS RD/CRM", desc: "Pipeline unificado de captura → RD Marketing → RD CRM com anotações. Histórico: substituído pelo FEO CRM em 04/11/2026.", to: "/admin/plano-leads-rd-crm", icon: Send, status: "concluido" },
  { title: "Tracking Meta → GTM", desc: "Migração completa do Meta Pixel + Conversions API do site para o Google Tag Manager. Registro técnico do que mudou.", to: "/admin/plano-tracking-gtm", icon: Activity, status: "concluido" },
  { title: "Bolsas FEO 2026", desc: "Prova online, correção, seleção e divulgação. Concluído, mantido como histórico e base para 2027.", to: "/admin/plano-bolsas-feo", icon: GraduationCap, status: "concluido" },
  { title: "Revisão pré-Black 2026", desc: "Revisão geral de banco, captação, integrações, velocidade e SEO antes de 05/10, com pontos de atenção.", to: "/admin/plano-revisao-pre-black", icon: Send, status: "concluido" },
  { title: "BLACK EM DOBRO", desc: "Pós em Dobro · R$ 1.997 à vista ou 12x de R$ 207 · meta 725 vendas e 5.000 leads · captura, envio (RD CRM + FEO CRM) e grupo wpp.", to: "/admin/plano-black-2x1", icon: BadgePercent, status: "concluido" },
  { title: "Roteador de Grupos WhatsApp", desc: "Links inteligentes da FEO que viram sozinhos para o próximo grupo ao bater a cota (850), com controle no painel. Sem ferramenta externa e sem chip conectado.", to: "/admin/plano-roteador-grupos", icon: Send, status: "futuro" },

];


function statusMeta(s: ProjetoStatus) {
  if (s === "concluido") return { accent: "#10b981", label: "Concluído" };
  if (s === "emandamento") return { accent: "#f5b022", label: "Em andamento" };
  return { accent: "#8b9bb5", label: "Futuro" };
}

function AdminProjetosPage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Dê um título ao projeto");
    toast.success("Projeto registrado como rascunho. Avise o Fision para criar a página de detalhe.");
    setTitle(""); setDesc(""); setShowForm(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 0%, rgba(11,10,31,0.45) 100%)" }} />
      <div className="relative px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-4">
              <img src={logoFeo} alt="Fisio em Ortopedia" className="h-10 w-auto drop-shadow-[0_4px_20px_rgba(245,176,34,0.3)]" />
              <div className="h-10 w-px bg-white/10" />
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">Planos e Implementações</h1>
                <p className="text-xs text-white/50 mt-0.5 uppercase tracking-widest">Painel de Controle · Projetos</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowForm((v) => !v)} className="bg-[#f5b022] border-[#f5b022] text-[#13123a] hover:bg-[#e09f17] hover:text-[#13123a] font-semibold">
                {showForm ? <X className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                {showForm ? "Cancelar" : "Novo projeto"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate({ to: "/admin" })} className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white">
                <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
              </Button>
            </div>
          </div>

          {showForm && (
            <form onSubmit={submit} className="mb-8 rounded-2xl border border-[#f5b022]/30 bg-white/5 backdrop-blur-sm p-6 animate-fade-in">
              <h2 className="text-lg font-bold text-white mb-1">Criar projeto/implementação futuro</h2>
              <p className="text-xs text-white/50 mb-4">Registra um rascunho. A página dedicada é criada depois pelo Fision.</p>
              <div className="grid gap-3">
                <div>
                  <Label className="text-white/70 text-xs">Título</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: App mobile do aluno" className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Descrição</Label>
                  <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder="Escopo, objetivo e contexto inicial..." className="bg-white/5 border-white/10 text-white" />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" className="bg-[#f5b022] hover:bg-[#e09f17] text-[#13123a] font-semibold">Salvar rascunho</Button>
                </div>
              </div>
            </form>
          )}

          {(["emandamento", "concluido", "futuro"] as ProjetoStatus[]).map((grupo) => {
            const itens = projetos.filter((p) => p.status === grupo);
            if (!itens.length) return null;
            const gm = statusMeta(grupo);
            const titulo = grupo === "emandamento" ? "EM ANDAMENTO" : grupo === "futuro" ? "FUTURO" : "CONCLUÍDOS";
            return (
              <section key={grupo} className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-2 w-2 rounded-full" style={{ background: gm.accent }} />
                  <h2 className="text-xs font-bold tracking-[0.25em]" style={{ color: gm.accent }}>{titulo}</h2>
                  <span className="text-xs text-white/40">{itens.length}</span>
                  <div className="h-px flex-1 bg-white/10" />
                </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {itens.map((p) => {
              const { accent, label } = statusMeta(p.status);
              const isDone = p.status === "concluido";
              return (
                <Link
                  key={p.to}
                  to={p.to}
                  className="group relative rounded-xl border p-5 transition-all duration-300 overflow-hidden"
                  style={{ borderColor: `${accent}33`, background: `linear-gradient(135deg, ${accent}0F, rgba(255,255,255,0.02))` }}
                >
                  <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `${accent}1A` }} />
                  <div className="relative flex items-start gap-4">
                    <div className="inline-flex items-center justify-center h-11 w-11 rounded-lg border group-hover:scale-110 transition-transform shrink-0" style={{ background: `${accent}26`, borderColor: `${accent}4D` }}>
                      <p.icon className="h-5 w-5" style={{ color: accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5" style={{ color: `${accent}CC` }}>
                        {isDone && <span>✓</span>}{label}
                      </div>
                      <h3 className="mt-0.5 text-base font-bold text-white">{p.title}</h3>
                      <p className="mt-1 text-sm text-white/60 leading-relaxed">{p.desc}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-white/40 group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                  </div>
                </Link>
              );
            })}
          </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
